/**
 * legacyFillFields — map a WL-012 V2 fill descriptor onto the LEGACY per-Segment
 * `settings` fields that the CURRENTLY-LIVE renderer reads, so a fill chosen in
 * the (flag-gated) Segment Styling pane ACTUALLY applies on the Wheel — not just
 * into the V2 `settings.fill` blob.
 *
 * Why this exists: the live segment renderer is `SegmentView/styles.js`, which
 * reads `settings.currentBg / bgWallpaper / segmentBgColor / isPattern` — NOT
 * `settings.fill` (that shape is only consumed by the not-yet-live WL-054
 * renderer-v2). The old, working Wallpaper controls apply a fill by writing those
 * legacy fields via `useSegmentSettings().handleChange` (hooks/useSegmentSetting.js).
 * This helper reproduces the EXACT same field writes so the new Image / GIF /
 * Pattern pickers land on the same proven, rendering path:
 *
 *   - Image  (old `addBgWallpaper`, side 0):  bgWallpaper=url, isPattern=0, currentBg=2
 *       → SegmentView styles: `background-image: url(bgWallpaper)` when currentBg===2
 *   - GIF:   same as image — a GIF is a background-image url that animates natively.
 *   - Pattern/gradient CSS (old `segmentBgGradientColor`): segmentBgColor=css, currentBg=3
 *       → SegmentView styles: `background: segmentBgColor` when currentBg===3
 *
 * Color (BUGFIX, daniyal-4847 2nd re-report / 4th site): the Color tab's own
 * swatch pick still writes only `settings.color` (the wedge frame colour,
 * `useSegmentSetting.js`'s "segmentColor" case) — that part is unchanged and
 * byte-for-byte the same as before unification. But the fill-TYPE tab switch
 * itself (`SegmentFillPanel.jsx`'s `setType` → `onChange({...fill, type})`)
 * DOES route through this function, and used to return {} unconditionally —
 * so switching from Image/GIF/Pattern/Gradient to Color left the PREVIOUS
 * fill's legacy bgWallpaper/currentBg untouched, and the deleted image kept
 * rendering AND round-tripping through the save. Color now unconditionally
 * asserts the canonical "no media" legacy state, same as Image/GIF's own
 * explicit-clear branch below.
 *
 * Gradient (BUGFIX, batch-11/12 no-persist finding): a Gradient fill has NO
 * OTHER legacy write path — the old `segmentBgGradientColor` handler
 * (hooks/useSegmentSetting.js) belongs to the retired Wallpaper accordion and
 * is never called by the new SegmentFillPanel/GradientBuilderPanel flow. Before
 * this fix `legacyFieldsForFill` returned {} for "gradient" unconditionally, so
 * `commitFill` wrote ONLY the V2 `settings.fill` blob — a key the BE
 * `ActivitySettings` model/serializer has no column for and silently drops
 * (DRF ModelSerializer ignores unrecognised input keys), so a chosen gradient
 * round-tripped to ZERO trace on GET-after-PUT. Fixed the same way Pattern
 * already works: derive the CSS via the canonical `buildSegmentFillCss` (the
 * SAME string the live preview swatch paints, so save = what-you-see) and
 * write it through the proven `segmentBgColor` + `currentBg=3` legacy path.
 *
 * Pattern (fast-follow to #2434, batch-11/12 no-persist finding): mirrors the
 * Gradient fix — derives the CSS via the SAME canonical `buildSegmentFillCss`
 * builder (`./segmentFill`) that the live preview swatch paints from, instead
 * of trusting a raw `pattern.css` field that the picker does not reliably set.
 * Before this fix the pattern branch read `f.pattern.css` directly, so a
 * pattern pick with `id`/`fg`/`bg` chosen but no (or a stale) `css` string
 * silently dropped everything except whatever color the swatch happened to
 * leak through — carrying the fill only PARTIALLY through the legacy bridge.
 * Deriving via the canonical builder makes save = what-you-see, exactly like
 * Image/GIF/Gradient already do.
 *
 * Pattern "None" clear (fix-editwheel-segment-fill-type-change-not-persisted,
 * live-reproduced on wheel 5578 seg 42100): the pattern branch's own
 * "nothing chosen yet" guard (`!p.id`) never distinguished the tab-switch case
 * (`f.pattern` entirely ABSENT) from BackgroundPatternPanel's "None" tile
 * explicitly clearing an already-chosen pattern (`f.pattern` PRESENT but `{}`,
 * the same shape `SegmentFillPanel.applyPattern` now commits for that click) —
 * both fell through to the identical `{}` no-op, so clicking "None" left
 * whatever pattern/gradient css was already saved completely untouched, and it
 * kept rendering/round-tripping through every subsequent save. Fixed with the
 * same explicit-clear-vs-untouched split Image/GIF already use just above.
 *
 * Pure: no React, no redux, no DOM. The caller (SegmentStylingPane.commitFill)
 * merges the returned fields into the SAME single `updateSegmentSetting` dispatch
 * as the V2 fill blob, keeping one undoable step.
 *
 * getPaletteColor note: the legacy `handleChange` runs values through
 * usePaletteColor().getPaletteColor, which returns any non-`suggested_color_*`
 * string unchanged. URLs and pattern-CSS are never palette tokens, so plain
 * assignment here is byte-equivalent to the legacy path.
 */

import { buildSegmentFillCss } from "./segmentFill";

// currentBg codes — the single source shared by useSegmentSetting.js (write) and
// SegmentView/styles.js (read).
export const BG_COLOR = 0; // solid color (segmentBgColor)
export const BG_PATTERN_OVERLAY = 1; // legacy numeric pattern overlay (patternId)
export const BG_IMAGE = 2; // background-image: url(bgWallpaper)
export const BG_GRADIENT_CSS = 3; // background: segmentBgColor (gradient / SVG-pattern css)

/**
 * @param {object} fill V2 fill descriptor `{ type, color?, gradient?, pattern?, image?, gif? }`
 * @returns {object} the legacy `settings` fields to merge (empty for color,
 *   or when the fill type has no concrete media/stops/css chosen yet — so
 *   switching to a tab before picking never clobbers the segment's current
 *   background).
 */
export const legacyFieldsForFill = (fill) => {
  const f = fill || {};

  if (f.type === "image") {
    const url = f.image && f.image.url;
    if (url) return { bgWallpaper: url, isPattern: 0, currentBg: BG_IMAGE };
    // daniyal-4847-photo-delete-resurrection (3rd duplicate site): `f.image`
    // PRESENT-but-empty (`{}`) is the exact shape SegmentFillPanel's
    // clearMedia("image") commits via its "Remove" button — distinct from the
    // media key being entirely ABSENT (a tab switch with nothing picked yet,
    // which must keep falling through to {} below so it never clobbers the
    // segment's current background). An explicit clear must actually clear the
    // legacy fields the live renderer AND the save payload read, the same way
    // the legacy Wallpaper accordion's own "Remove image" already does
    // (utils/segmentWallpaper.js resolveBgWallpaperMode: empty -> currentBg=0).
    if (f.image) return { bgWallpaper: "", isPattern: 0, currentBg: BG_COLOR };
    return {};
  }

  if (f.type === "gif") {
    const url = f.gif && f.gif.url;
    if (url) return { bgWallpaper: url, isPattern: 0, currentBg: BG_IMAGE };
    // Same explicit-clear vs untouched distinction as image, above.
    if (f.gif) return { bgWallpaper: "", isPattern: 0, currentBg: BG_COLOR };
    return {};
  }

  if (f.type === "pattern") {
    const p = f.pattern;
    // Pattern key entirely ABSENT (tab switch, nothing picked yet) → never
    // clobber the segment's current background — same guard image/gif/gradient
    // already apply.
    if (!p) return {};
    if (!p.id) {
      // Pattern key PRESENT but id unset is the "None" tile's explicit-clear
      // shape (BackgroundPatternPanel.handleClear → SegmentFillPanel.applyPattern
      // commits `pattern: {}`) — DISTINCT from the tab-switch case above, same
      // explicit-clear-vs-untouched split image/gif already use. Before this fix
      // both shapes fell through the same `!p.id` guard, so clicking "None" was a
      // silent no-op: legacyFieldsForFill returned {}, the segment's prior
      // pattern/gradient css was never overwritten, and it kept rendering/
      // round-tripping through the save (live-measured on wheel 5578 seg 42100:
      // clicking None left segment_bg_color as the untouched prior gradient css
      // across an 18s poll + full reload).
      return { segmentBgColor: "", isPattern: 0, currentBg: BG_COLOR };
    }
    const css = buildSegmentFillCss(f);
    if (!css) return {};
    return { segmentBgColor: css, currentBg: BG_GRADIENT_CSS };
  }

  if (f.type === "gradient") {
    // Only once the user has actually placed stops — an empty/default gradient
    // state must never clobber the segment's current background on tab switch
    // (same guard image/gif/pattern already apply).
    const stops = f.gradient && f.gradient.stops;
    if (!Array.isArray(stops) || stops.length === 0) return {};
    const css = buildSegmentFillCss(f);
    if (!css) return {};
    return { segmentBgColor: css, currentBg: BG_GRADIENT_CSS };
  }

  if (f.type === "color") {
    // daniyal-4847-photo-delete-resurrection (2nd re-report, 4th site — the
    // Segment Fill panel's fill-TYPE tablist itself): the Color tab is a
    // definitive, unambiguous choice — unlike Gradient/Pattern (multi-step
    // pickers that need the "nothing chosen yet" guard, see above), there is
    // no partial/undecided state once the user is on Color. So, UNLIKE those
    // guarded branches, this must NOT return {} — unconditionally asserting
    // the canonical "no media/pattern/gradient" legacy state (bgWallpaper="",
    // currentBg=BG_COLOR) is what actually clears a stale Image/GIF/Pattern/
    // Gradient background left over from before the user switched tabs. The
    // old `{}` here (measured live: builder/4806 seg 21299) meant clicking
    // the Color tab — or picking a color, which never itself calls
    // legacyFieldsForFill; only the tab switch's own onChange({...fill,
    // type:"color"}) does — left an already-applied image's bgWallpaper/
    // currentBg=2 completely untouched, so the deleted image kept rendering
    // AND kept round-tripping through the save (bg_wallpaper/current_bg in
    // the PUT body never changed). Harmless to write unconditionally: when
    // there was nothing to clear, bgWallpaper is already "" and currentBg
    // already BG_COLOR, so this is a no-op overwrite in that case.
    return { bgWallpaper: "", isPattern: 0, currentBg: BG_COLOR };
  }

  // unknown → no legacy fields (that path is untouched).
  return {};
};

export default legacyFieldsForFill;
